import type { Header } from "@tanstack/react-table";
import { flexRender } from "@tanstack/react-table";
import { useRef } from "react";
import { cn } from "../../lib/utils";
import { useTableHeaderColumnSizing } from "../DataTable/useTableHeaderColumnSizing";
import { ColumnHeaderFilterDropdown } from "../FilterableDataTable/ColumnHeaderFilterDropdown";
import { Separator } from "../Separator";

type DynamicDataTableHeaderCellProps<TData, TValue> = {
	header: Header<TData, TValue>;
	className?: string;
};

export const DynamicDataTableHeaderCell = <TData, TValue>({
	header,
	className,
}: DynamicDataTableHeaderCellProps<TData, TValue>) => {
	const cellRef = useRef<HTMLDivElement>(null);
	const dropdownTriggerRef = useRef<HTMLButtonElement>(null);
	const clickXRef = useRef<number | undefined>(undefined);
	const columnSize = useTableHeaderColumnSizing(header, cellRef);
	const canResize = header.column.getCanResize();
	const isLastColumn = header.index === header.headerGroup.headers.length - 1;
	const hasDropdown =
		header.column.getCanSort() || header.column.columnDef.meta?.headerFilterType !== undefined;

	return (
		<div
			className={cn(
				"h-full flex items-center text-[13px] font-medium text-secondary pl-[15px] cursor-pointer min-w-fit truncate [&:nth-last-child(1)]:flex-1",
				header.column.id === "expand" ? "px-0" : "",
				className,
			)}
			ref={cellRef}
			style={{ width: columnSize, minWidth: header.column.columnDef.minSize }}
		>
			<div
				className={cn(
					"flex-1 flex items-center h-full",
					header.getContext().column.columnDef.header ? "justify-between" : "justify-center",
				)}
				onClick={
					hasDropdown
						? (e: React.MouseEvent) => {
								clickXRef.current = e.clientX;
								dropdownTriggerRef.current?.click();
							}
						: undefined
				}
			>
				<div>{flexRender(header.column.columnDef.header, header.getContext())}</div>
				{hasDropdown && (
					<ColumnHeaderFilterDropdown
						column={header.column}
						triggerRef={dropdownTriggerRef}
						clickXRef={clickXRef}
						anchorRef={cellRef}
					/>
				)}
			</div>
			<div
				className={cn("flex flex-row items-center px-1 shrink-0", canResize && "cursor-ew-resize")}
				onMouseDown={canResize ? header.getResizeHandler() : undefined}
			>
				{canResize && (
					<Separator orientation="vertical" className="h-[18px] bg-bc-strong mr-[2px]" />
				)}
				<Separator
					orientation="vertical"
					className={cn("h-[18px] bg-bc-strong", isLastColumn && !canResize && "hidden")}
				/>
			</div>
		</div>
	);
};
